import {
  Box,
  Avatar,
  Text,
  Flex,
  Icon,
  Button,
  IconButton,
  useToast,
} from "@chakra-ui/react";

import {useState, useEffect} from "react";
import {confirmAppointment} from "../../crudRequests";
import {FaCheck} from "react-icons/fa";
import {FiX, FiCalendar, FiClock, FiMapPin, FiVideo, FiPhone} from "react-icons/fi";
import CancelAppointmentModal from "./CancelAppointmentModal";
import RescheduleAppointmentModal from "./RescheduleAppointmentModal";
import {
  checkCancellationPolicy,
  cancelAppointment,
  rescheduleAppointment,
} from "../../crudRequests";
import {getJitsiJWT} from "./helpers/jitsiFunctions";
import {getPatientMeetingLink} from "./helpers/getPatientMeetingLink";
import {clinicianDisplayName} from "../../utils/clinicianName";

import {IoVideocam} from "react-icons/io5";
import {FcGoogle} from "react-icons/fc";
import {SiMicrosoftoutlook} from "react-icons/si";
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import {
  appointmentInstant,
  appointmentMinutes,
  legacyDateString,
  namedTimeString,
  resolveAppointmentTimeZone,
} from "../../services/schedule/appointmentClock";
import {deliveryModeLabel, deliveryModeOfAppointment} from "../../services/schedule/deliveryMode";

export default function Appointment({
  state,
  appointment,
  transaction,
  setPreview,
  setPreviewInvRec,
  setSelectedTr,
  dispatch,
  setApptNotes,
  apptDoctor,
}) {
  const patient = state.patient;
  const dashState = state;
  const toast = useToast();

  const isGroupAppointment = appointment.appointmentCategory === "group";
  const isHost = !isGroupAppointment || appointment.pid === patient?.pid;
  const participantConfirmed = isGroupAppointment
    ? appointment.participantStatuses?.[patient?.pid]?.pConfirmed
    : appointment.pConfirmed;

  const [payment, setPayment] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(participantConfirmed);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  // Bumped when the server refuses a time as no longer available, so the modal
  // reloads its options instead of leaving the patient looking at a list the
  // server has already disagreed with.
  const [rescheduleReloadKey, setRescheduleReloadKey] = useState(0);
  const [cancellationPolicy, setCancellationPolicy] = useState(null);
  const [loadingPolicy, setLoadingPolicy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [loadingFor, setLoadingFor] = useState(null);

  useEffect(() => {
    setPayment(transaction);
  }, [transaction]);

  useEffect(() => {
    setIsConfirmed(participantConfirmed);
  }, [participantConfirmed]);

  const handleConfirmAppointment = async () => {
    setConfirming(true);
    try {
      await confirmAppointment(dashState, appointment.aid);
      setIsConfirmed(true);
      toast({
        title: "Appointment confirmed",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error confirming appointment",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setConfirming(false);
    }
  };

  const fetchCancellationPolicy = async () => {
    setLoadingPolicy(true);
    try {
      const response = await checkCancellationPolicy(dashState, appointment.aid);
      setCancellationPolicy(response.data);
    } catch (error) {
      console.error("Error fetching cancellation policy:", error);
      setCancellationPolicy({
        canModify: false,
        blockedMessage: "Unable to check cancellation policy. Please try again.",
        clinicPhone: null,
      });
    } finally {
      setLoadingPolicy(false);
    }
  };

  const handleOpenCancelModal = async () => {
    setLoadingFor("cancel");
    await fetchCancellationPolicy();
    setShowCancelModal(true);
    setLoadingFor(null);
  };

  const handleOpenRescheduleModal = async () => {
    setLoadingFor("reschedule");
    await fetchCancellationPolicy();
    setShowRescheduleModal(true);
    setLoadingFor(null);
  };

  const handleCancelAppointment = async (reason) => {
    setCancelling(true);
    try {
      await cancelAppointment(dashState, appointment.aid, reason);

      toast({
        title: "Appointment cancelled",
        description: "Your appointment has been cancelled successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setShowCancelModal(false);

      if (dispatch) {
        dispatch({
          type: "UPDATE_APPOINTMENT",
          payload: {
            ...appointment,
            cancelled: true,
            cancelledAt: Date.now(),
            cancellationReason: reason,
            cancelledBy: "patient",
          },
        });
      }
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      const data = error.response?.data;
      // The backend now routes patient cancellations through the canonical
      // cancellation cascade, which refuses appointments with financial
      // activity (a payment or an active insurance claim) with a 409
      // `HAS_FINANCIAL_ACTIVITY`. Show the patient a friendly "contact the
      // clinic" message instead of the staff-facing "cancel from the
      // workspace" wording.
      const description =
        data?.code === "HAS_FINANCIAL_ACTIVITY"
          ? "This appointment can't be cancelled online because it has a payment or an active insurance claim. Please contact the clinic to cancel it."
          : data?.blockedMessage ||
            data?.message ||
            data?.error ||
            "Failed to cancel appointment. Please try again.";
      toast({
        title: "Error",
        description,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setCancelling(false);
    }
  };

  const handleRescheduleAppointment = async (updateData) => {
    setRescheduling(true);
    try {
      // The server writes the reschedule, drops the previous confirmation and
      // notifies the patient, the clinician, and the outgoing clinician when
      // one is replaced.
      const result = await rescheduleAppointment(
        dashState,
        appointment.aid,
        updateData
      );

      const clinicianChanged = updateData.did && updateData.did !== appointment.did;
      const savedAppointment = result?.data?.appointment;
      const updatedAppointment = savedAppointment
        ? {...appointment, ...savedAppointment}
        : {...appointment, ...updateData, rescheduled: true, pConfirmed: false};

      // The new time from the instant it moved to, on the clinic's clock, named.
      const newWhen = `${legacyDateString(updateData.startAt, apptTimeZone)} at ${namedTimeString(updateData.startAt, apptTimeZone)}`;
      const description = clinicianChanged
        ? `Your appointment has been rescheduled to ${newWhen} with ${updateData.dName}.`
        : `Your appointment has been rescheduled to ${newWhen}.`;

      toast({
        title: "Appointment rescheduled",
        description,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setShowRescheduleModal(false);

      if (dispatch) {
        dispatch({type: "UPDATE_APPOINTMENT", payload: updatedAppointment});
      }
    } catch (error) {
      console.error("Error rescheduling appointment:", error);

      // `AVAILABILITY_CONFLICT` is not a failure to be retried: the server
      // checked the time against the same engine the options came from, so it
      // means what this screen is showing is out of date — somebody took the
      // slot, or the clinician's schedule changed while the modal was open.
      // The message is written to be read by a patient, so it is shown as it
      // arrived, under a title that says what happened rather than "Error".
      const conflict = error.response?.data?.error === "AVAILABILITY_CONFLICT";
      toast({
        title: conflict ? "That time is no longer available" : "Error",
        description:
          error.response?.data?.blockedMessage ||
          error.response?.data?.message ||
          "Failed to reschedule appointment. Please try again.",
        status: conflict ? "warning" : "error",
        duration: 7000,
        isClosable: true,
      });
      // Leave the modal open and let it reload: the patient picks again from
      // options that are current.
      if (conflict) setRescheduleReloadKey((key) => key + 1);
    } finally {
      setRescheduling(false);
    }
  };

  // --- Status logic ---
  const status = appointment.arrived
    ? {name: "Arrived", color: "green", dot: "#34a853"}
    : appointment.noShow
    ? {name: "No Show", color: "red", dot: "#e53e3e"}
    : appointment.cancelled
    ? {name: "Cancelled", color: "red", dot: "#e53e3e"}
    : appointment.rescheduled
    ? {name: "Rescheduled", color: "orange", dot: "#e8a23e"}
    : isConfirmed
    ? {name: "Confirmed", color: "green", dot: "#34a853"}
    : {name: "Unconfirmed", color: "gray", dot: "#9ca3af"};

  const canConfirm =
    !isConfirmed && !appointment.cancelled && !appointment.arrived && !appointment.noShow;
  // How the visit takes place (§2.7): a video visit has the meeting link, a
  // phone visit is a call — never read from a link the visit happens to keep.
  const mode = deliveryModeOfAppointment(appointment) || "in-person";
  const isVirtual = mode === "videoconference";
  const isTelephone = mode === "telephone";
  const meetingLink = getPatientMeetingLink(appointment, patient?.pid);
  const canModifyAppointment =
    !appointment.cancelled &&
    !appointment.arrived &&
    !appointment.noShow &&
    appointment.startAt > Date.now() &&
    isHost;

  // One name for the clinician on every surface of this visit — the card, its
  // avatar, the reschedule window, the calendar export (utils/clinicianName.js).
  const knownDoctorName = clinicianDisplayName({doctor: apptDoctor, appointment, fallback: ""});
  const doctorName = knownDoctorName || "Your clinician";

  const appointmentType = deliveryModeLabel(mode);

  const typeIcon = isVirtual ? FiVideo : isTelephone ? FiPhone : FiMapPin;

  // Derived from `startAt` on the clinic's clock — never from the legacy
  // `date` / `time` mirrors, which are being retired and can hold an epoch.
  const apptTimeZone = resolveAppointmentTimeZone({
    appointment,
    locations: state?.locations,
    organization: state?.organization,
  });
  const apptDateLabel = legacyDateString(
    appointmentInstant(appointment),
    apptTimeZone
  );
  // "7:00 PM ET": the clinic's clock, named.
  const apptTimeLabel = namedTimeString(
    appointmentInstant(appointment),
    apptTimeZone
  );
  // Its length is its span (§2.6), never the `duration` label.
  const apptMinutes = appointmentMinutes(appointment);

  // --- Calendar helpers ---
  // The appointment's instant, straight off `startAt`. This used to rebuild the
  // moment by parsing the legacy `date` / `time` strings, which cannot express
  // an hour reliably and throw on the rows that carry an epoch in `date`.
  const parseAppointmentDate = () => {
    const at = appointmentInstant(appointment);
    if (!at) throw new Error("This appointment has no start time.");
    return new Date(at);
  };

  const addToGoogleCalendar = () => {
    let appointmentDate;
    try {
      appointmentDate = parseAppointmentDate();
    } catch (error) {
      toast({title: "Date Error", description: error.message, status: "error", duration: 5000, isClosable: true});
      return;
    }

    // The event ends when the visit does.
    const durationMinutes = apptMinutes ?? 0;
    const endDateTime = new Date(appointmentDate.getTime() + durationMinutes * 60 * 1000);
    const formatDate = (date) => date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const providerName = doctorName;
    const title = appointment.service ? `${appointment.service} - Medical Appointment` : "Medical Appointment";
    const details = `Appointment with ${providerName}\n${apptDateLabel} at ${apptTimeLabel || "TBD"}\nDuration: ${durationMinutes} min\nType: ${appointmentType}`;
    const locationStr = isVirtual ? "Video appointment" : isTelephone ? "Phone appointment" : state.selectedLocation ? `${state.selectedLocation.name || ""}, ${state.selectedLocation.address || ""}` : "";

    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${formatDate(appointmentDate)}/${formatDate(endDateTime)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(locationStr)}`;
    window.open(googleUrl, "_blank");
    toast({title: "Opening Google Calendar", status: "success", duration: 3000, isClosable: true});
  };

  const addToOutlookCalendar = () => {
    let appointmentDate;
    try {
      appointmentDate = parseAppointmentDate();
    } catch (error) {
      toast({title: "Date Error", description: error.message, status: "error", duration: 5000, isClosable: true});
      return;
    }

    // The event ends when the visit does.
    const durationMinutes = apptMinutes ?? 0;
    const endDateTime = new Date(appointmentDate.getTime() + durationMinutes * 60 * 1000);
    const formatDate = (date) => date.toISOString().split(".")[0];
    const providerName = doctorName;
    const title = appointment.service ? `${appointment.service} - Medical Appointment` : "Medical Appointment";
    const body = `Appointment with ${providerName}\n${apptDateLabel} at ${apptTimeLabel || "TBD"}\nDuration: ${durationMinutes} min\nType: ${appointmentType}`;
    const locationStr = isVirtual ? "Video appointment" : isTelephone ? "Phone appointment" : state.selectedLocation ? `${state.selectedLocation.name || ""}, ${state.selectedLocation.address || ""}` : "";

    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(title)}&startdt=${formatDate(appointmentDate)}&enddt=${formatDate(endDateTime)}&body=${encodeURIComponent(body)}&location=${encodeURIComponent(locationStr)}`;
    window.open(outlookUrl, "_blank");
    toast({title: "Opening Outlook Calendar", status: "success", duration: 3000, isClosable: true});
  };

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="8px"
      p={4}
      transition="all 0.15s ease"
      _hover={{borderColor: "gray.300", boxShadow: "sm"}}
    >
      {/* Row 1: Status + Date + Calendar menu */}
      <Flex align="center" justify="space-between" mb={3}>
        <Flex align="center" gap={2}>
          <Flex
            align="center"
            gap={1.5}
            px={2}
            py={0.5}
            borderRadius="full"
            bg={`${status.color}.50`}
            w="fit-content"
          >
            <Box w="5px" h="5px" borderRadius="full" bg={status.dot} />
            <Text fontSize="11px" fontWeight="500" color={`${status.color}.600`}>
              {status.name}
            </Text>
          </Flex>
          <Flex align="center" gap={1} color="#6b7280">
            <Icon as={typeIcon} fontSize="12px" />
            <Text fontSize="11px" fontWeight="500">
              {appointmentType}
            </Text>
          </Flex>
        </Flex>

        {/* Calendar add menu */}
        <Menu placement="bottom-end">
          <MenuButton
            as={IconButton}
            icon={<FiCalendar size={13} />}
            size="xs"
            variant="ghost"
            color="#6b7280"
            _hover={{color: "#6772e5", bg: "#f0f0ff"}}
            borderRadius="6px"
            aria-label="Add to calendar"
          />
          <MenuList
            minW="180px"
            shadow="lg"
            borderRadius="8px"
            borderColor="#e6e6e6"
            py={1}
          >
            <MenuItem
              fontSize="12px"
              icon={<FcGoogle size={14} />}
              onClick={addToGoogleCalendar}
              _hover={{bg: "#f8f9fa"}}
            >
              Google Calendar
            </MenuItem>
            <MenuItem
              fontSize="12px"
              icon={<SiMicrosoftoutlook size={14} color="#0078d4" />}
              onClick={addToOutlookCalendar}
              _hover={{bg: "#f8f9fa"}}
            >
              Outlook Calendar
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      {/* Row 2: Doctor + appointment info */}
      <Flex align="center" gap={3} mb={3}>
        <Avatar
          size="sm"
          name={knownDoctorName || undefined}
          src={apptDoctor?.photo}
          bg="#6772e5"
          color="white"
          fontSize="xs"
        />
        <Box flex={1} minW={0}>
          <Text fontSize="13px" fontWeight="600" color="#1a1f36" noOfLines={1}>
            {doctorName}
          </Text>
          <Text fontSize="12px" color="#6b7280" noOfLines={1}>
            {appointment.service?.name || appointment.service || "Consultation"}
          </Text>
        </Box>
      </Flex>

      {/* Row 3: Date & Time */}
      <Flex
        bg="gray.50"
        borderRadius="6px"
        px={3}
        py={2}
        mb={3}
        align="center"
        justify="space-between"
      >
        <Flex align="center" gap={2}>
          <Icon as={FiCalendar} fontSize="13px" color="#6b7280" />
          <Text fontSize="12px" fontWeight="500" color="#1a1f36">
            {apptDateLabel}
          </Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Icon as={FiClock} fontSize="13px" color="#6b7280" />
          <Text fontSize="12px" fontWeight="500" color="#1a1f36">
            {apptTimeLabel}
          </Text>
          {apptMinutes !== null && (
            <Text fontSize="11px" color="#6b7280">
              ({apptMinutes} min)
            </Text>
          )}
        </Flex>
      </Flex>

      {/* Row 4: Action buttons */}
      <Flex gap={2} flexWrap="wrap">
        {canConfirm && (
          <Button
            size="xs"
            bg="#34a853"
            color="white"
            _hover={{bg: "#2d9249"}}
            leftIcon={<FaCheck size={10} />}
            onClick={handleConfirmAppointment}
            isLoading={confirming}
            loadingText="Confirming"
            borderRadius="6px"
            fontSize="11px"
            fontWeight="500"
          >
            Confirm
          </Button>
        )}
        {isVirtual && meetingLink && !appointment.cancelled && (
          <Button
            size="xs"
            bg="#6772e5"
            color="white"
            _hover={{bg: "#5b63d3"}}
            leftIcon={<IoVideocam size={12} />}
            onClick={() => {
              if (appointment?.virtualMeeting?.platform === "jitsi") {
                window.open(meetingLink, "_blank");
                return;
              }

              if (appointment?.patientApptLink) {
                void getJitsiJWT(
                  dashState,
                  appointment?.aid,
                  patient?.pid,
                  dashState?.db,
                  apptDoctor,
                  appointment?.patientApptLink
                );
              }
            }}
            borderRadius="6px"
            fontSize="11px"
            fontWeight="500"
          >
            Join Call
          </Button>
        )}
        {canModifyAppointment && (
          <>
            <Button
              size="xs"
              variant="ghost"
              color="#6b7280"
              _hover={{color: "#6772e5", bg: "#f0f0ff"}}
              leftIcon={<FiCalendar size={12} />}
              onClick={handleOpenRescheduleModal}
              isLoading={loadingFor === "reschedule"}
              borderRadius="6px"
              fontSize="11px"
              fontWeight="500"
            >
              Reschedule
            </Button>
            <Button
              size="xs"
              variant="ghost"
              color="#6b7280"
              _hover={{color: "#e53e3e", bg: "red.50"}}
              leftIcon={<FiX size={12} />}
              onClick={handleOpenCancelModal}
              isLoading={loadingFor === "cancel"}
              borderRadius="6px"
              fontSize="11px"
              fontWeight="500"
            >
              Cancel
            </Button>
          </>
        )}
      </Flex>

      {/* Modals */}
      <CancelAppointmentModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        appointment={appointment}
        when={`${apptDateLabel} at ${apptTimeLabel}`}
        canModify={cancellationPolicy?.canModify}
        blockedMessage={cancellationPolicy?.blockedMessage}
        clinicPhone={cancellationPolicy?.clinicPhone}
        onConfirm={handleCancelAppointment}
        isLoading={cancelling}
      />
      <RescheduleAppointmentModal
        isOpen={showRescheduleModal}
        onClose={() => setShowRescheduleModal(false)}
        appointment={appointment}
        canModify={cancellationPolicy?.canModify}
        blockedMessage={cancellationPolicy?.blockedMessage}
        clinicPhone={cancellationPolicy?.clinicPhone}
        onConfirm={handleRescheduleAppointment}
        isLoading={rescheduling}
        state={dashState}
        reloadKey={rescheduleReloadKey}
      />
    </Box>
  );
}
